<template>
  <section
    v-if="isLoaded && conditions.length > 0"
    class="p-the-h01-grant-criteria-panel"
    aria-labelledby="grant-criteria-title"
  >
    <h2 id="grant-criteria-title" class="p-the-h01-grant-criteria-panel__title">
      付与基準例
    </h2>

    <div
      v-for="condition in conditions"
      :key="condition.id"
      class="p-the-h01-grant-criteria-panel__item"
    >
      <div class="p-the-h01-grant-criteria-panel__icon">
        <IconInvestedRounded
          :type="iconType(condition)"
          :icon-size="36"
          :bg-color="colorCode"
          icon-color="#ffffff"
        />
      </div>
      <div class="p-the-h01-grant-criteria-panel__body">
        <span class="p-the-h01-grant-criteria-panel__name" data-translate-skip>
          {{ condition.name }}
        </span>
        <div
          class="p-the-h01-grant-criteria-panel__point"
          data-translate-skip
        >
          <span class="p-the-h01-grant-criteria-panel__point__sign">+</span>
          <div class="p-the-h01-grant-criteria-panel__point__body">
            <span class="p-the-h01-grant-criteria-panel__point__value">
              {{ addComma(condition.points) }}
            </span>
            <span class="p-the-h01-grant-criteria-panel__point__unit">
              {{ pointName }}
            </span>
          </div>
        </div>
        <p
          v-if="condition.text"
          class="p-the-h01-grant-criteria-panel__text"
          data-translate-skip
        >
          {{ condition.text }}
        </p>
      </div>
    </div>

    <div class="p-the-h01-grant-criteria-panel__footer">
      <a :href="urlGrantCriteria" class="p-the-h01-grant-criteria-panel__more">
        <span class="p-the-h01-grant-criteria-panel__more__label">
          付与基準をもっと見る
        </span>
        <IconBase
          class="p-the-h01-grant-criteria-panel__more__icon"
          view-box="0 0 24 24"
          :width="24"
          :height="24"
          :icon-color="colorCode"
        >
          <IconArrowRight />
        </IconBase>
      </a>
    </div>
  </section>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import IconBase from "@/common/components/atoms/IconBase.vue";
import IconArrowRight from "@/common/components/icons/IconArrowRight.vue";
import IconInvestedRounded from "@/common/components/icons/IconInvestedRounded.vue";
import { addComma as addCommaFilter } from "@/common/assets/js/filters";
import commons from "@/common/store/modules/commons";
import CONST_DATA from "@/general/mixins/const/constData";
import { getGrantCriteriaList } from "@/general/store/api";
import { GrantCriteria } from "@/general/store/models";

@Options({
  name: "TheH01GrantCriteriaPanel",
  components: {
    IconBase,
    IconArrowRight,
    IconInvestedRounded
  }
})
export default class TheH01GrantCriteriaPanel extends Vue {
  conditions: Array<GrantCriteria> = [];
  isLoaded = false;

  get isGroupBudget() {
    return commons?.userInfo.groupBudgetMode;
  }

  get colorCode() {
    if (this.isGroupBudget) {
      return "#fc9014";
    }
    return commons.colorCode;
  }

  get pointName() {
    return commons.OrgPageSpecific?.point_name || commons?.unit?.point;
  }

  get urlGrantCriteria() {
    return (
      CONST_DATA.SITE_MAP.GRANT_CRITERIA + "?redirect_from=point-confirmation"
    );
  }

  async created() {
    const response = await getGrantCriteriaList("", 1, 3, "");
    this.conditions = response.results || [];
    this.isLoaded = true;
  }

  iconType(condition: GrantCriteria) {
    return condition.grant_criteria_icon_id || 1;
  }

  addComma = addCommaFilter;
}
</script>

<style lang="scss" scoped>
.p-the-h01-grant-criteria-panel {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  width: 100%;
  background: $common-white;
  border: px(2) solid #f4f5f6;
  border-radius: rem(8);
  box-shadow: 0 0 rem(4) rgba(0, 0, 0, 0.1);
  color: $common-black;
  font-family: "Noto Sans JP", sans-serif;

  @include large-pc {
    gap: rem(24);
    padding: rem(24) rem(16);
  }

  @include large-sp {
    gap: rem(16);
    padding: rem(16);
  }

  &__title {
    margin: 0;
    font-size: rem(20);
    font-weight: 700;
    line-height: 1.25;
    text-align: center;
    font-feature-settings: "pwid" 1, "palt" 1;

    @include sp {
      font-size: rem(16);
    }
  }

  &__item {
    display: flex;
    align-items: flex-start;
    gap: rem(16);
    min-width: 0;
  }

  &__icon {
    display: flex;
    flex: 0 0 rem(36);
  }

  &__body {
    display: flex;
    flex: 1 1 0;
    flex-direction: column;
    gap: rem(4);
    min-width: 0;
  }

  &__name {
    display: -webkit-box;
    overflow: hidden;
    font-size: rem(16);
    font-weight: 700;
    line-height: 1.25;
    font-feature-settings: "pwid" 1, "palt" 1;
    overflow-wrap: anywhere;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;

    @include sp {
      font-size: rem(14);
    }
  }

  &__point {
    display: flex;
    align-items: center;
    gap: rem(2);
    color: $common-primary;

    &__sign {
      font-size: rem(16);
      font-weight: 400;
      line-height: 1;

      @include sp {
        font-size: rem(14);
      }
    }

    &__body {
      display: flex;
      align-items: flex-end;
      gap: rem(2);
      white-space: nowrap;
    }

    &__value {
      font-size: rem(16);
      font-weight: 700;
      line-height: 1;
      font-feature-settings: "palt" 1, "pwid" 1;

      @include sp {
        font-size: rem(14);
      }
    }

    &__unit {
      font-size: rem(10);
      font-weight: 700;
      line-height: 1;
      font-feature-settings: "palt" 1, "pwid" 1;

      @include sp {
        font-size: rem(8);
      }
    }
  }

  &__text {
    display: -webkit-box;
    overflow: hidden;
    margin: 0;
    font-size: rem(14);
    font-weight: 400;
    line-height: 1.8;
    white-space: pre-line;
    font-feature-settings: "palt" 1, "pwid" 1;
    overflow-wrap: anywhere;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }

  &__footer {
    display: flex;
    justify-content: center;
  }

  &__more {
    display: flex;
    align-items: center;
    gap: rem(4);
    padding: rem(8);
    background: $common-white;
    border: px(2) solid $common-black;
    border-radius: rem(4);
    color: $common-black;

    &__label {
      font-size: rem(16);
      font-weight: 700;
      line-height: 1.25;
      text-align: center;
      font-feature-settings: "pwid" 1, "palt" 1;

      @include sp {
        font-size: rem(14);
      }
    }
  }
}
</style>
